export const LOCALES = ['fr', 'en', 'es', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];
export type Messages = Record<string, string>;
export const LANGUAGE_NAMES: Record<Locale, string> = { fr: 'Français', en: 'English', es: 'Español', ru: 'Русский' };
export const LANGUAGE_TAGS: Record<Locale, string> = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES', ru: 'ru-RU' };
export const isLocale = (value: unknown): value is Locale => typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
export const localeFromPath = (path: string): Locale => { const first = path.split('/')[1]; return isLocale(first) ? first : 'fr'; };
export const stripLocale = (path: string): string => path.replace(/^\/(en|es|ru|fr)(?=\/|[?#]|$)/, '') || '/';
export function isPublicPath(path: string): boolean {
  return path === '/' || /^\/(ecosysteme|methode-sx|a-propos)\/?$/.test(path) || /^\/(rise|r-node|sx-business|elina)(?:\/decouvrir(?:\/(entreprises|associations|formation|institutions))?)?\/?$/.test(path) || /^\/r-node\/focus(?:\/(ventes-mobilite|achats-transit|gestion-stocks))?\/?$/.test(path);
}
/** Only public routes are localized. Private workspaces and API URLs keep their access rules. */
export function localizeHref(href: string, locale: Locale): string {
  if (!href || href.startsWith('#') || href.startsWith('?')) return href;
  let origin = '', rest = href;
  if (/^https:\/\/(www\.)?rdvpartners\.fr(?:\/|$)/.test(href)) { const u = new URL(href); origin = u.origin; rest = u.pathname + u.search + u.hash; }
  else if (/^https:\/\/rnode\.rdvpartners\.fr(?:\/|$)/.test(href)) {
    const u = new URL(href);
    const path = stripLocale(u.pathname).replace(/\/index\.html$/, '/');
    if (!/^\/(?:negoce-distribution|industrie|menuiserie-aluminium|architecture|maintenance|multisites|organismes-de-formation|conseil-accompagnement)?\/?$/.test(path)) return href;
    const route = path === '/' ? '/' : path.replace(/\/$/, '') + '/';
    u.pathname = (locale === 'fr' ? '' : '/' + locale) + route;
    u.searchParams.delete('lang');
    return u.toString();
  }
  else if (!href.startsWith('/') || href.startsWith('//')) return href;
  const match = rest.match(/^([^?#]*)(.*)$/)!;
  const path = stripLocale(match[1]);
  if (!isPublicPath(path)) return href;
  return origin + (locale === 'fr' ? path : '/' + locale + (path === '/' ? '' : path)) + match[2];
}
export const normalizeMessage = (text: string) => text.replace(/\s+/g, ' ').trim();
export function createTranslator(messages: Messages = {}) {
  return (source: string, values?: Record<string, string | number>): string => {
    const key = normalizeMessage(source);
    const translated = messages[key];
    // Preserve intentional spacing/newlines around fragments used in rich text.
    let output = translated === undefined ? source : source.match(/^\s*/)?.[0] + translated + source.match(/\s*$/)?.[0];
    if (values) output = output.replace(/\{(\w+)\}/g, (placeholder, key: string) => Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : placeholder);
    return output;
  };
}
export type Translator = ReturnType<typeof createTranslator>;
/** Use only for our static editorial data, never visitor content or AI responses. */
export function translateContent<T>(value: T, t: Translator): T {
  if (typeof value === 'string') return t(value) as T;
  if (Array.isArray(value)) return value.map(item => translateContent(item, t)) as T;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, translateContent(item, t)])) as T;
  return value;
}



